import { z } from 'zod'

import { calculate1RM } from '../../lib/analysis'
import { formatWeightValue, getWeightUnit } from '../../lib/unit-utils'
import type { AITool } from './types'
import type { WorkoutExercise } from '../../types'

const paramsSchema = z.object({
  exerciseTemplateID: z.string().min(1).describe('The exercise template ID to analyze'),
  days: z
    .number()
    .min(7)
    .max(365)
    .describe('Number of days to analyze. Valid range: 7-365')
})

const resultSchema = z.object({
  exerciseName: z.string().describe('Name of the exercise'),
  daysSinceLastPerformed: z.number().describe('Days since last performed this exercise'),
  totalSessions: z.number().describe('Total number of sessions in the time period'),
  frequency: z.string().describe('How often this exercise is performed'),
  currentTrend: z.object({
    direction: z.string().describe('Trend direction: improving, maintaining, or declining'),
    percentageChange: z.number().describe('Percentage change in 1RM over the period'),
    recentBest1RM: z.number().describe('Best estimated 1RM from recent sessions'),
    allTimeBest1RM: z.number().describe('All-time best estimated 1RM')
  }),
  recentHistory: z.array(
    z.object({
      date: z.string().describe('Workout date in YYYY-MM-DD format'),
      daysAgo: z.number().describe('How many days ago this workout was'),
      sets: z.array(
        z.object({
          weight: z.number().describe('Weight used in user preferred units'),
          reps: z.number().describe('Repetitions completed'),
          estimated1RM: z.number().describe('Estimated 1RM from this set')
        })
      ),
      bestSet: z.object({
        weight: z.number(),
        reps: z.number(),
        estimated1RM: z.number()
      }),
      totalVolume: z.number().describe('Total volume (weight × reps) for this session')
    })
  ),
  lastWorkout: z.object({
    weight: z.number().describe('Weight used in most recent best set'),
    reps: z.number().describe('Reps completed in most recent best set')
  }),
  summary: z.string().describe('Basic factual summary of exercise history')
})

export const getExerciseHistory: AITool = {
  name: 'getExerciseHistory',
  description: 'Get exercise history with trends and recent workouts.',
  parameters: paramsSchema,
  execute: async (params: any, context: any) => {
    const { exerciseTemplateID, days } = paramsSchema.parse(params)
    const { workoutData, userSettings } = context
    const unitSystem = userSettings?.settings?.units || 'metric'
    const isMetric = unitSystem === 'metric'

    // Find exercise name from workouts
    let exerciseName: string | null = null
    let exerciseType: 'weighted' | 'bodyweight' | 'assisted' = 'weighted'
    
    // Collect all workout sessions with this exercise
    const exerciseSessions: Array<{
      date: Date
      sets: Array<{ weight_kg: number; reps: number }>
    }> = []

    // Look through workouts to find exercises with this template ID
    if (workoutData.workouts) {
      for (const workout of workoutData.workouts) {
        const exercisesInWorkout = workout.exercises?.filter(
          (ex: WorkoutExercise) => ex.exercise_template_id === exerciseTemplateID
        ) || []
        
        if (exercisesInWorkout.length > 0) {
          // Get exercise name from first occurrence
          if (!exerciseName) {
            exerciseName = exercisesInWorkout[0].title
            // Determine exercise type
            const firstSet = exercisesInWorkout[0].sets?.[0]
            if (firstSet) {
              if (firstSet.weight_kg === 0) {
                exerciseType = 'bodyweight'
              } else if ((exerciseName || '').toLowerCase().includes('assist')) {
                exerciseType = 'assisted'
              }
            }
          }
          
          // Collect all sets from this workout
          const allSets = exercisesInWorkout.flatMap((ex: WorkoutExercise) => 
            ex.sets?.map((set: any) => ({
              weight_kg: set.weight_kg || 0,
              reps: set.reps || 0
            })) || []
          )
          
          if (allSets.length > 0) {
            exerciseSessions.push({
              date: new Date(workout.start_time),
              sets: allSets
            })
          }
        }
      }
    }

    if (!exerciseName || exerciseSessions.length === 0) {
      throw new Error(`No workout history found for exercise template ID ${exerciseTemplateID}. Use lookupExerciseTemplateID first.`)
    }

    // Sort sessions by date (newest first)
    exerciseSessions.sort((a, b) => b.date.getTime() - a.date.getTime())

    // Filter by date range
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)
    const filteredSessions = exerciseSessions.filter(
      (session) => session.date >= cutoffDate
    )

    // Calculate metrics
    const today = new Date()
    const lastSession = exerciseSessions[0] // Most recent
    const daysSinceLastPerformed = Math.floor(
      (today.getTime() - lastSession.date.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Calculate frequency
    const totalDays = days
    const sessionsInPeriod = filteredSessions.length
    const daysPerSession = sessionsInPeriod > 0 ? totalDays / sessionsInPeriod : totalDays
    let frequency = 'rarely'
    if (daysPerSession <= 4) frequency = 'frequently (2+ times/week)'
    else if (daysPerSession <= 7) frequency = 'weekly'
    else if (daysPerSession <= 14) frequency = 'biweekly'
    else if (daysPerSession <= 30) frequency = 'monthly'

    // Calculate 1RM trends
    let allTime1RM = 0
    let recent1RM = 0
    let firstPeriod1RM = 0

    // Process all sessions for history
    const recentHistory = filteredSessions.slice(0, 10).map((session) => {
      const daysAgo = Math.floor((today.getTime() - session.date.getTime()) / (1000 * 60 * 60 * 24))
      
      const sets = session.sets.map((set) => {
        const weight = set.weight_kg * (isMetric ? 1 : 2.20462)
        const estimated1RM = exerciseType === 'bodyweight' ? set.reps : calculate1RM(set.weight_kg, set.reps)
        
        // Track all-time best
        if (estimated1RM > allTime1RM) allTime1RM = estimated1RM
        
        return {
          weight: Math.round(weight * 10) / 10,
          reps: set.reps,
          estimated1RM: Math.round(estimated1RM * (isMetric ? 1 : 2.20462) * 10) / 10
        }
      })

      // Find best set by 1RM
      const bestSet = sets.reduce((best, set) => 
        set.estimated1RM > best.estimated1RM ? set : best
      )

      const totalVolume = session.sets.reduce(
        (sum, set) => sum + set.weight_kg * set.reps * (isMetric ? 1 : 2.20462),
        0
      )

      return {
        date: session.date.toISOString().split('T')[0],
        daysAgo,
        sets,
        bestSet,
        totalVolume: Math.round(totalVolume)
      }
    })

    // Calculate trend from first 3 and last 3 sessions in period
    const recentSessions = filteredSessions.slice(0, 3)
    const oldSessions = filteredSessions.slice(-3)

    recentSessions.forEach((session) => {
      session.sets.forEach((set) => {
        const est1RM = exerciseType === 'bodyweight' ? set.reps : calculate1RM(set.weight_kg, set.reps)
        if (est1RM > recent1RM) recent1RM = est1RM
      })
    })

    oldSessions.forEach((session) => {
      session.sets.forEach((set) => {
        const est1RM = exerciseType === 'bodyweight' ? set.reps : calculate1RM(set.weight_kg, set.reps)
        if (est1RM > firstPeriod1RM) firstPeriod1RM = est1RM
      })
    })

    // Calculate trend
    const percentageChange = firstPeriod1RM > 0
      ? Math.round(((recent1RM - firstPeriod1RM) / firstPeriod1RM) * 1000) / 10
      : 0

    let direction = 'maintaining'
    if (percentageChange > 2) direction = 'improving'
    else if (percentageChange < -2) direction = 'declining'

    // Last workout data
    const lastBestSet = recentHistory[0]?.bestSet

    // Simple factual summary
    const summary = `${exerciseName}: ${sessionsInPeriod} sessions in ${days} days. ${direction} trend (${percentageChange > 0 ? '+' : ''}${percentageChange}%).`

    return {
      exerciseName,
      daysSinceLastPerformed,
      totalSessions: sessionsInPeriod,
      frequency,
      currentTrend: {
        direction,
        percentageChange,
        recentBest1RM: Math.round(recent1RM * (isMetric ? 1 : 2.20462) * 10) / 10,
        allTimeBest1RM: Math.round(allTime1RM * (isMetric ? 1 : 2.20462) * 10) / 10
      },
      recentHistory,
      lastWorkout: {
        weight: lastBestSet?.weight || 0,
        reps: lastBestSet?.reps || 0
      },
      summary
    }
  }
}